import React from 'react';
import { Platform } from 'react-native';
import { createStackNavigator } from '@react-navigation/stack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LoginScreen } from '@/screens/residents/auth/LoginScreen';
import { UserSelectionScreen } from '@/screens/residents/auth/UserSelectionScreen.native';
import { ForgotPasswordScreen } from '@/screens/residents/auth/ForgotPasswordScreen';
import { AccountVerificationScreen } from '@/screens/residents/auth/AccountVerificationScreen';
import { SignUpScreen } from '@/screens/residents/auth/SignUpScreen';
import { VerificationScreen } from '@/screens/residents/auth/VerificationScreen.native';
import { ResetPasswordScreen } from '@/screens/residents/auth/ResetPasswordScreen';
import { LegalScreen, LegalContentType } from '@/screens/residents/legal';
import { useResponsive } from '@/hooks/common/useResponsive';
import { AuthDesktopLayout } from '@/components/auth/AuthDesktopLayout';
import { LoginDesktopContent } from '@/screens/residents/auth/LoginScreen.web';
import { ForgotPasswordDesktopContent } from '@/screens/residents/auth/ForgotPasswordScreen.web';
import { ResetPasswordDesktopContent } from '@/screens/residents/auth/ResetPasswordScreen.web';
import { AccountVerificationDesktopContent } from '@/screens/residents/auth/AccountVerificationScreen.web';
import { SignUpDesktopContent } from '@/screens/residents/auth/SignUpScreen.web';

export type AuthStackParamList = {
  Login: undefined;
  UserSelection: undefined;
  Signup: undefined;
  ForgotPassword: { email?: string };
  AccountVerification: { email: string; isSignup?: boolean };
  ResetPassword: { token: string };
  Verification: { email: string; isSignup: boolean };
  Legal: { contentType: LegalContentType };
};

const Stack = createStackNavigator<AuthStackParamList>();

const LEGAL_SCREEN_OPTIONS = {
  headerShown: true,
  headerTitle: '',
  headerBackTitleVisible: false,
  headerStyle: {
    backgroundColor: '#FFFFFF',
    shadowColor: 'transparent',
    elevation: 0,
  },
};

const AuthMobileNavigator: React.FC = () => {
  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: Platform.OS === 'web' ? '#171717' : '#FFFFFF' }} edges={['bottom']}>
      <Stack.Navigator
        screenOptions={{
          headerShown: false,
          cardStyle: { backgroundColor: Platform.OS === 'web' ? '#171717' : '#FFFFFF' },
          ...(Platform.OS === 'web'
            ? { animationEnabled: false as const }
            : {}),
        }}
      >
        <Stack.Screen name="Login" component={LoginScreen} />
        <Stack.Screen name="UserSelection" component={UserSelectionScreen} />
        <Stack.Screen name="Signup" component={SignUpScreen} />
        <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} />
        <Stack.Screen name="AccountVerification" component={AccountVerificationScreen} />
        <Stack.Screen name="ResetPassword" component={ResetPasswordScreen} />
        <Stack.Screen name="Verification" component={VerificationScreen} />
        <Stack.Screen name="Legal" component={LegalScreen} options={LEGAL_SCREEN_OPTIONS} />
      </Stack.Navigator>
    </SafeAreaView>
  );
};

const AuthDesktopNavigator: React.FC = () => {
  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#171717' }} edges={['bottom']}>
      <AuthDesktopLayout>
        <Stack.Navigator
          screenOptions={{
            headerShown: false,
            cardStyle: { backgroundColor: '#171717' },
            animationEnabled: false,
          }}
        >
          <Stack.Screen name="Login" component={LoginDesktopContent} />
          <Stack.Screen name="Signup" component={SignUpDesktopContent} />
          <Stack.Screen name="ForgotPassword" component={ForgotPasswordDesktopContent} />
          <Stack.Screen name="AccountVerification" component={AccountVerificationDesktopContent} />
          <Stack.Screen name="ResetPassword" component={ResetPasswordDesktopContent} />
          <Stack.Screen name="UserSelection" component={UserSelectionScreen} />
          <Stack.Screen name="Verification" component={VerificationScreen} />
          <Stack.Screen name="Legal" component={LegalScreen} options={LEGAL_SCREEN_OPTIONS} />
        </Stack.Navigator>
      </AuthDesktopLayout>
    </SafeAreaView>
  );
};

const AuthWebNavigator: React.FC = () => {
  const { layoutMode } = useResponsive();

  if (layoutMode === 'desktop') {
    return <AuthDesktopNavigator />;
  }
  return <AuthMobileNavigator />;
};

export const AuthNavigator: React.FC = () => {
  if (Platform.OS === 'web') {
    return <AuthWebNavigator />;
  }
  return <AuthMobileNavigator />;
};
